import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

export interface ArrivalWindowFields {
  arrivalWindowStart: string | null
  arrivalWindowEnd: string | null
  timeLockedAt: string | null
}

export interface ArrivalWindowState {
  /** "8–12" in Helsinki wall-clock hours. */
  window: string
  /** The route planner may still move the time (and the sweeper). */
  flexible: boolean
}

/**
 * The promise we make once the time is fixed: a 45-minute band starting at the
 * planned start floored to a 15-minute grid, e.g. "9.30–10.15" for a 9.40
 * plan. Deliberately coarser than the plan: the sweeper always has over 30
 * minutes of slack after the planned start before we are late on paper.
 */
export function arrivalBand(startTime: string | Date): {
  from: string
  to: string
} {
  const start = dayjs(startTime).tz('Europe/Helsinki')
  const from = start.minute(Math.floor(start.minute() / 15) * 15)
  return {
    from: from.format('H.mm'),
    to: from.add(45, 'minute').format('H.mm'),
  }
}

/** "8:00–12:00" in Helsinki wall-clock time — the promised window everywhere. */
export function formatArrivalWindow(window: {
  start: string
  end: string
}): string {
  const fmt = (t: string) => dayjs(t).tz('Europe/Helsinki').format('H:mm')
  return `${fmt(window.start)}–${fmt(window.end)}`
}

/**
 * How a window booking stands: the promised window and whether its time is
 * still provisional. Null for ordinary exact-time visits.
 */
export function getArrivalWindowState(
  visit: ArrivalWindowFields,
): ArrivalWindowState | null {
  if (!visit.arrivalWindowStart || !visit.arrivalWindowEnd) return null
  return {
    window: formatArrivalWindow({
      start: visit.arrivalWindowStart,
      end: visit.arrivalWindowEnd,
    }),
    flexible: !visit.timeLockedAt,
  }
}
